<template>
  <Teleport to="body">
    <Transition name="app-banner">
      <div v-if="modelValue" class="app-banner" :class="`app-banner--${variant}`" :role="role" aria-live="polite">
        <div class="app-banner__body">
          <p v-if="title" class="app-banner__title">{{ title }}</p>
          <p v-if="message || $slots.default" class="app-banner__message">
            <slot>{{ message }}</slot>
          </p>
        </div>

        <div v-if="$slots.actions" class="app-banner__actions">
          <slot name="actions" />
        </div>

        <button
          v-if="dismissible"
          type="button"
          class="app-banner__close"
          :aria-label="dismissLabel"
          @click="dismiss"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, watch } from "vue"

/**
 * App-level banner pinned to the top of the viewport, above the app chrome.
 *
 * Use this for a message that belongs to the *app*, not to a panel — the guest
 * upgrade prompt, a subscription notice, a "you are viewing someone else's
 * household" hint. For a notice that belongs inside a card or form, use
 * `AppAlert` instead; it flows with the content and does not float.
 *
 * Why it teleports: the app shell is a flex container that is `column` on
 * mobile and `row` at the `md` breakpoint (`#app` in style.css). A banner
 * rendered as a child of that shell becomes a *flex item* — it lands at the
 * bottom of the page on mobile and stretches into a full-height second column
 * on desktop. Teleporting to <body> takes it out of that layout entirely, and
 * is also what keeps `position: fixed` honest inside PullToRefresh's transform.
 *
 * z-index comes from `--z-banner` (above the header and bottom nav, below
 * modals and toasts) — never a literal number.
 */
const props = withDefaults(
  defineProps<{
    /** Whether the banner is shown. Two-way, so `v-model` dismisses it. */
    modelValue: boolean
    variant?: "accent" | "info" | "warning" | "danger"
    title?: string
    /** Body copy. The default slot wins if both are given. */
    message?: string
    /** Show the ✕ button. Actions in the `actions` slot are independent. */
    dismissible?: boolean
    /** Accessible name for the ✕ — required reading for screen readers. */
    dismissLabel?: string
    /** ARIA role; defaults to "alert" for warning/danger, "status" otherwise. */
    role?: string
  }>(),
  { variant: "accent", dismissible: false, dismissLabel: "Dismiss" },
)

const emit = defineEmits<{
  "update:modelValue": [value: boolean]
  dismiss: []
}>()

const role = computed(
  () => props.role ?? (props.variant === "danger" || props.variant === "warning" ? "alert" : "status"),
)

function dismiss() {
  emit("update:modelValue", false)
  emit("dismiss")
}

// A persistent banner must not sit on top of the page it is about — overlaying
// the <h1> reads as broken. So publish our measured height as
// `--app-banner-height` and let the shell reserve that much room (#app's
// padding-top in style.css). Measured rather than hardcoded because the copy
// wraps to two rows on narrow screens.
let observer: ResizeObserver | null = null

function publishHeight(px: number) {
  document.documentElement.style.setProperty("--app-banner-height", `${px}px`)
}

function clearHeight() {
  document.documentElement.style.removeProperty("--app-banner-height")
}

function measure() {
  const node = document.querySelector<HTMLElement>(".app-banner")
  if (!node) {
    clearHeight()
    return
  }
  // + the 0.5rem the banner is inset from the top, so content clears it.
  publishHeight(node.offsetHeight + 8)
  observer?.disconnect()
  observer = new ResizeObserver(() => publishHeight(node.offsetHeight + 8))
  observer.observe(node)
}

watch(
  () => props.modelValue,
  async (open) => {
    if (!open) {
      observer?.disconnect()
      observer = null
      clearHeight()
      return
    }
    // Wait for Transition + Teleport to put the node in the document.
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)))
    measure()
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  observer?.disconnect()
  clearHeight()
})
</script>

<style scoped>
.app-banner {
  position: fixed;
  /* Below the fixed mobile header, which owns the top 56px and sits at
     --z-header. On desktop that header is hidden, so the offset drops away
     (see the md override at the bottom of this block). */
  top: calc(56px + var(--safe-top) + 0.5rem);
  left: 50%;
  transform: translateX(-50%);
  z-index: var(--z-banner);

  display: flex;
  align-items: center;
  gap: var(--spacing-4);
  /* Wide enough for a title + copy + two actions, still clearly a banner and
     not a dialog. The viewport clamp keeps it inside small screens. */
  width: calc(100vw - 1.5rem);
  max-width: 38rem;
  padding: var(--spacing-3) var(--spacing-4);
  border-radius: 6px;
  box-shadow: var(--shadow-lg);
}

.app-banner--accent {
  background: var(--accent-subtle);
  border: 1px solid var(--accent);
  color: var(--text-primary);
}

/* Same token triples AppAlert uses, so a banner and an inline alert of the
   same severity read as the same thing. */
.app-banner--info {
  background: var(--steel-100);
  border: 1px solid var(--steel-200);
  color: var(--steel-700);
}

.app-banner--warning {
  background: var(--amber-100);
  border: 1px solid var(--amber-200);
  color: var(--amber-700);
}

.app-banner--danger {
  background: var(--ruby-100);
  border: 1px solid var(--ruby-200);
  color: var(--ruby-700);
}

.app-banner__body {
  flex: 1;
  min-width: 0;
}

.app-banner__title {
  margin: 0;
  font-size: var(--text-sm);
  font-weight: 700;
  color: inherit;
}

.app-banner__message {
  margin: 0;
  font-size: var(--text-xs);
  color: inherit;
  opacity: 0.85;
}

.app-banner__actions {
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  flex-shrink: 0;
}

.app-banner__close {
  flex-shrink: 0;
  background: none;
  border: none;
  padding: 0.25rem;
  color: inherit;
  opacity: 0.7;
  cursor: pointer;
  border-radius: 3px;
}

.app-banner__close:hover {
  opacity: 1;
}

/* Below md the actions cannot sit beside the copy without squeezing both to
   nothing, so the banner becomes two rows. Mobile is the primary target. */
@media (max-width: 640px) {
  .app-banner {
    flex-wrap: wrap;
    gap: var(--spacing-2);
  }

  .app-banner__body {
    /* Take the full first row, leaving the ✕ pinned to its right. */
    flex-basis: calc(100% - 2rem);
  }

  .app-banner__actions {
    width: 100%;
  }
}

/* The mobile header is hidden from md up, so reclaim its 56px. Matches the
   breakpoint style.css uses to switch #app to a row. */
@media (min-width: 768px) {
  .app-banner {
    top: calc(var(--safe-top) + 0.5rem);
  }
}

.app-banner-enter-active,
.app-banner-leave-active {
  transition:
    opacity 0.25s ease,
    transform 0.25s ease;
}

.app-banner-enter-from,
.app-banner-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-8px);
}
</style>
